import { fmtMoney, fmtPct, fmtMonthLong, cn } from '../utils.js';
import { FundLogo } from './FundChip.js';
import type { FundCurrency } from '@agio/core/domain';

export interface Kpi {
  label: string;
  value: string;
  tone?: 'green' | 'red' | 'plain';
  /** Small caption under the value — e.g. the month a figure refers to. */
  sub?: string;
  /** When set, render an overlapping avatar group (shadcn avatar-group style) instead of the bare number. */
  avatars?: string[];
}

export function kpiFromNumbers(args: {
  totalValue: number | null;
  monthlyReturn: number | null;
  monthlyBenchmark: number | null;
  /** Month the RETURN figures refer to (ISO) — the last month with a computed return. */
  asOf?: string | null;
  /**
   * Month the BALANCE refers to (ISO) — the newest snapshot being shown.
   *
   * Separate from `asOf` on purpose. A balance moves forward as soon as a
   * statement is published; a return only appears once that month's deposits
   * and withdrawals are confirmed. Stamping one date on both meant the balance
   * card showed this month's money under an older month's name.
   *
   * REQUIRED, not optional, even though it falls back to `asOf` when null: a
   * caller that forgets it silently reintroduces the bug, and a review caught
   * exactly that in the printed report. Required means the compiler catches it
   * instead. Pass null deliberately if there is genuinely no balance month.
   */
  balanceAsOf: string | null;
  /** Month the BENCHMARK return refers to (ISO) — its own series can end elsewhere. Required for the same reason. */
  benchAsOf: string | null;
  /** null = not known yet (shown as a dash, never as 0). */
  fundCount: number | null;
  fundNames?: string[];
  /** Fund view: replaces the "בתי השקעות פעילים" card with this fund's active span.
   * A null `to` renders as "היום" (still active); a null `from` renders as "—". */
  period?: { from: string | null; to: string | null } | null;
  /** Fund view: the currency the balance is in. The whole-portfolio total stays in shekels. */
  currency?: FundCurrency;
}): Kpi[] {
  const tone = (v: number | null): Kpi['tone'] => (v === null ? 'plain' : v >= 0 ? 'green' : 'red');
  const month = args.asOf ? fmtMonthLong(args.asOf) : undefined;
  const balanceMonth = args.balanceAsOf ? fmtMonthLong(args.balanceAsOf) : month;
  const benchMonth = args.benchAsOf ? fmtMonthLong(args.benchAsOf) : month;
  const fourth: Kpi = args.period
    ? { label: 'משך פעילות', value: periodLabel(args.period), tone: 'plain' }
    : { label: 'בתי השקעות פעילים', value: args.fundCount === null ? '—' : String(args.fundCount), tone: 'plain', avatars: args.fundNames };
  return [
    { label: 'שווי כולל', value: fmtMoney(args.totalValue, args.currency), tone: 'green', sub: balanceMonth && `נכון ל־${balanceMonth}` },
    { label: 'תשואה חודשית', value: fmtPct(args.monthlyReturn), tone: tone(args.monthlyReturn), sub: month },
    { label: "מדד ייחוס חודשי", value: fmtPct(args.monthlyBenchmark), tone: tone(args.monthlyBenchmark), sub: benchMonth },
    fourth,
  ];
}

/** "12/2015 – היום", a single month when the span is one month, or "—" when unknown. */
function periodLabel({ from, to }: { from: string | null; to: string | null }): string {
  if (!from) return '—';
  const start = fmtMonthLong(from);
  if (!to) return `${start} – היום`;
  const end = fmtMonthLong(to);
  return start === end ? start : `${start} – ${end}`;
}

/** Overlapping fund-logo avatars — https://ui.shadcn.com/docs/components/base/avatar (group variant):
 *  each avatar overlaps the previous and sits on top of it, ring in the card color, +N chip last. */
function AvatarGroup({ names }: { names: string[] }) {
  const shown = names.slice(0, 5);
  const extra = names.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((n, i) => (
        <span
          key={n}
          title={n}
          className="relative inline-flex rounded-full ring-2 ring-card"
          style={{ marginInlineStart: i === 0 ? 0 : -10, zIndex: i + 1 }}
        >
          <FundLogo name={n} size={36} />
        </span>
      ))}
      {extra > 0 && (
        <span
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground ring-2 ring-card"
          style={{ marginInlineStart: -10, zIndex: shown.length + 1 }}
          title={names.slice(5).join(', ')}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

export function KpiCards({ kpis }: { kpis: Kpi[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((k) => (
        <div
          key={k.label}
          className="kpi-tile relative overflow-hidden rounded-xl border border-border bg-card p-5 shadow-(--shadow-card) transition duration-150 hover:border-ring/60 hover:-translate-y-0.5"
        >
          <span
            aria-hidden
            className="absolute inset-y-0 start-0 w-[3px] bg-gradient-to-b from-[#4aa3ff] to-[#3ee0a4] opacity-80"
          />
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{k.label}</div>
          {k.avatars?.length ? (
            <div className="mt-2 flex items-center gap-3">
              <AvatarGroup names={k.avatars} />
              <span className="font-mono text-2xl font-semibold tabular-nums">{k.value}</span>
            </div>
          ) : (
            <>
              <div
                className={cn(
                  'mt-1.5 font-mono text-2xl font-semibold tabular-nums tracking-tight',
                  k.tone === 'green' && 'text-success',
                  k.tone === 'red' && 'text-destructive',
                )}
              >
                {k.value}
              </div>
              {k.sub && <div className="mt-1.5 text-xs text-muted-foreground">{k.sub}</div>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
